import React from "react";
import Header from "../../components/Header";
import "./Login.css";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { ForgotPasswordPageProps, ForgotPasswordPageState, ForgotPasswordInputFieldProps, NavigateFunction } from "../../types";

// Define InputField component outside to prevent recreation
const InputField: React.FC<ForgotPasswordInputFieldProps> = ({ type, value, onChange, placeholder, className, ...rest }) => (
    <div className={`user-box ${className || ''}`}>
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={className}
            {...rest}
        />
    </div>
);

// Create a wrapper component to use hooks
function ForgotPasswordPageWrapper(props: { darkMode: boolean }): React.ReactElement {
    const navigate = useNavigate();
    return <ForgotPasswordPage {...props} navigate={navigate} />;
}

class ForgotPasswordPage extends React.Component<ForgotPasswordPageProps, ForgotPasswordPageState> {
    constructor(props: ForgotPasswordPageProps) {
        super(props);
        this.state = {
            email: "",
            error: null,
            success: null,
            isLoading: false
        };

        this.sendResetEmail = this.sendResetEmail.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    onChange = (field: keyof ForgotPasswordPageState) => (e: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({
            [field]: e.target.value,
            error: null // Clear error when user starts typing
        } as any);
    }

    sendResetEmail = async (): Promise<void> => {
        const { email } = this.state;

        if (!email) {
            this.setState({ error: "Please enter your email address" });
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            this.setState({ error: "Please enter a valid email address" });
            return;
        }

        this.setState({ isLoading: true, error: null, success: null });

        try {
            const auth = getAuth();
            await sendPasswordResetEmail(auth, email);
            this.setState({
                success: "Password reset email sent! Check your inbox and follow the instructions to reset your password.",
                isLoading: false
            });
        } catch (error: any) {
            let errorMessage = "An error occurred. Please try again.";

            switch (error.code) {
                case 'auth/user-not-found':
                    errorMessage = "No account found with this email address.";
                    break;
                case 'auth/invalid-email':
                    errorMessage = "Please enter a valid email address.";
                    break;
                case 'auth/too-many-requests':
                    errorMessage = "Too many attempts. Please try again later.";
                    break;
                default:
                    errorMessage = error.message;
            }

            this.setState({
                error: errorMessage,
                isLoading: false
            });
        }
    }

    render(): React.ReactElement {
        const { darkMode } = this.props;
        const { email, error, success, isLoading } = this.state;

        const getClassName = (base: string, darkVariant: string): string => darkMode ? darkVariant : base;

        return (
            <div className="loginTop" data-dark-mode={darkMode}>
                <Header darkMode={darkMode} />
                <div className="login">
                    <h2 className="page-title">Reset Password</h2>

                    <p style={{
                        color: darkMode ? '#15b5e2' : '#fff',
                        marginBottom: '20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        opacity: 0.8
                    }}>
                        Enter your email address and we'll send you a link to reset your password.
                    </p>

                    <InputField
                        type="email"
                        value={email}
                        onChange={this.onChange('email')}
                        placeholder="Email Address"
                    />

                    {/* Error Display */}
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {/* Success Display */}
                    {success && (
                        <div style={{
                            background: 'rgba(76, 175, 80, 0.1)',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '20px',
                            color: '#4CAF50',
                            fontFamily: 'Avenir Next, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
                            fontSize: '14px',
                            fontWeight: '500',
                            textAlign: 'center',
                            backdropFilter: 'blur(10px)'
                        }}>
                            {success}
                        </div>
                    )}

                    <div className="loginBtn">
                        <button
                            className={getClassName("letsRun-login", "letsRun-dark-login")}
                            onClick={this.sendResetEmail}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Sending...' : 'Send Reset Email'}
                        </button>
                    </div>

                    <div className="signup-link">
                        <p>Remember your password? <Link to="/login" className="signup-link-text">Back to Login</Link></p>
                    </div>
                </div>
            </div>
        );
    }
}

export default ForgotPasswordPageWrapper;
